
import { Logger, hashEmail, getPageName } from "../../helpers";
import { EVENTS } from "./CONSTANTS";
import { TRACKERS } from "./trackingFlags";

/**
 * @fileoverview Manages event data, preparing payloads and handling user data.
 * @module DataManager
 */

/**
 * Cleans an object by removing keys with null or undefined values.
 * @param {Object} data - The raw user data object.
 * @returns {Object} The cleaned object without null or undefined fields.
 */
const cleanFields = (data) => {
  const cleaned = {};
  for (const key in data) {
    if (data[key] !== null && data[key] !== undefined) {
      cleaned[key] = data[key];
    }
  }
  return cleaned;
};

/**
 * The `DataManager` class handles the preparation and management of event data,
 * including user data such as hashed emails, tracking parameters, and payloads for events.
 *
 * ### High-Level Logic:
 * - Prepares event payloads with common data.
 * - Handles hashing of emails, extraction of tracking parameters (fbq, fbc), and user identification.
 *
 * ### Structure:
 * - The class interacts with session storage to store and retrieve user data.
 * - Provides methods to prepare payloads for different types of events.
 *
 * @example
 * const dataManager = new DataManager();
 * dataManager.initialize();
 * const payload = dataManager.prepareCommonPayload(EVENTS.PageView, {}, "event_id_123");
 */
class DataManager {
  constructor() {
    this.hashEmail = hashEmail;
    this.sessionStorageKeys = {
      userData: "userData",
    };
  }

  /**
   * Initializes the DataManager by ensuring user data is present in session storage.
   * @async
   * @method initialize
   * @memberof DataManager
   */
  async initialize() {
    if (!sessionStorage.getItem(this.sessionStorageKeys.userData)) {
      await this.updateUserData();
    }
  }

  /**
   * Updates user data, hashing the email if provided, and stores it in session storage.
   * @async
   * @method updateUserData
   * @memberof DataManager
   * @param {string} [email=null] - The user's email to hash and store.
   */
  async updateUserData(email = null) {
    const userData = await this.getUserData();

    if (email && TRACKERS.SERVER_TRACKING) {
      Logger.log("Hashing email:", email);
      userData.hashedEmail = await this.hashEmail(email);
      sessionStorage.setItem("user_email", userData.hashedEmail);
    }

    if (!TRACKERS.SERVER_TRACKING) {
      userData.fbq = null;
      userData.fbc = null;
      userData.hashedEmail = null;
      sessionStorage.removeItem("user_email");
    }

    sessionStorage.setItem(
      this.sessionStorageKeys.userData,
      JSON.stringify(userData)
    );

    return userData;
  }

  /**
   * Retrieves user data from session storage or initializes if server tracking is disabled.
   * @async
   * @method getUserData
   * @memberof DataManager
   * @returns {Object} The user data object.
   */
  async getUserData() {
    if (!TRACKERS.SERVER_TRACKING) {
      Logger.info("Skipping user data collection due to disabled server tracking.");
      return {
        fbq: null,
        fbc: null,
        hashedEmail: null,
        external_id: "anonymous",
      };
    }

    const storedData = JSON.parse(
      sessionStorage.getItem(this.sessionStorageKeys.userData) || "{}"
    );

    const fbq = await this.getFbq();
    const fbc = await this.getFbc();
    const hashedEmail = await this.getHashedEmail();

    const updatedData = {
      ...storedData,
      fbq: fbq || storedData.fbq || null,
      fbc: fbc || storedData.fbc || null,
      hashedEmail: hashedEmail || storedData.hashedEmail || null,
    };

    if (JSON.stringify(updatedData) !== JSON.stringify(storedData)) {
      sessionStorage.setItem(
        this.sessionStorageKeys.userData,
        JSON.stringify(updatedData)
      );
    }

    return updatedData;
  }

  /**
   * Retrieves the hashed email from session storage.
   * @async
   * @method getHashedEmail
   * @memberof DataManager
   * @returns {Promise<string|null>} The hashed email or null if not available.
   */
  async getHashedEmail() {
    return sessionStorage.getItem("user_email") || null;
  }

  /**
   * Extracts the fbq tracking parameter from cookies or the document referrer.
   * @async
   * @method getFbq
   * @memberof DataManager
   * @returns {string|null} The fbq value or null if not found.
   */
  async getFbq() {
    const fbqCookie = this.getCookie("_fbp");
    Logger.log("Retrieved fbq:", fbqCookie);
    if (fbqCookie) return fbqCookie;

    const referrer = document.referrer;
    if (referrer.includes("facebook.com")) {
      const extractedFbq = this.extractFbqFromReferrer(referrer);
      Logger.log("Extracted fbq from referrer:", extractedFbq);
      return extractedFbq;
    }

    return null;
  }

  /**
   * Helper to extract fbq from referrer string.
   * @method extractFbqFromReferrer
   * @memberof DataManager
   * @param {string} referrer
   * @returns {string|null}
   */
  extractFbqFromReferrer(referrer) {
    try {
      const url = new URL(referrer);
      return url.searchParams.get("fbq") || null;
    } catch (error) {
      Logger.error("Error extracting fbq from referrer:", error);
      return null;
    }
  }

  /**
   * Extracts the fbc tracking parameter from cookies or the URL.
   * @async
   * @method getFbc
   * @memberof DataManager
   * @returns {string|null} The fbc value or null if not found.
   */
  async getFbc() {  
    const fbcCookie = this.getCookie("_fbc");
    Logger.log("Retrieved fbc:", fbcCookie);
    if (fbcCookie) return fbcCookie;

    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get("fbclid");
    Logger.log("Retrieved fbclid from URL:", fbclid);
    return fbclid || null;
  }

  /**
   * Retrieves a specific cookie by name.
   * @method getCookie
   * @memberof DataManager
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string|null} The cookie value or null if not found.
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  }

  /**
   * Prepares a common payload for tracking events.
   * @async
   * @method prepareCommonPayload
   * @memberof DataManager
   * @param {string} eventName - The event name to track.
   * @param {Object} customData - Custom data to include in the payload.
   * @param {string} eventId - The unique ID for the event, generated in Analytics.
   * @returns {Object} The prepared event payload.
   */
  async prepareCommonPayload(eventName, customData, eventId) {
    const rawUserData = await this.getUserData();
    const consentData = JSON.parse(decodeURIComponent(this.getCookie("consentData") || "{}"));

    const userData = cleanFields({
      external_id: rawUserData.external_id || "anonymous",
      client_user_agent: navigator.userAgent,
      fbq: rawUserData.fbq,
      fbc: rawUserData.fbc,
      hashedEmail: rawUserData.hashedEmail,
    });

    return {
      "event-type": eventName,
      eventId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      source: getPageName(),
      userData,
      customData,
      noTrack: consentData.hasOptedOut,
    };
  }

  /**
   * Prepares the payload for a PageView event.
   * @method preparePageViewEventData
   * @memberof DataManager
   * @param {string} eventId - The unique ID for the event.
   * @returns {Object} The prepared event payload for PageView.
   */
  preparePageViewEventData(eventId) {
    return this.prepareCommonPayload(EVENTS.PageView, {}, eventId);
  }

  /**
   * Prepares the payload for a FindLocation event.
   * @method prepareFindLocationEventData
   * @memberof DataManager
   * @param {string} eventId - The unique ID for the event.
   * @returns {Object} The prepared event payload for FindLocation.
   */
  prepareFindLocationEventData(eventId) {
    return this.prepareCommonPayload(EVENTS.FindLocation, {}, eventId);
  }

  /**
   * Prepares the payload for a NewsletterSubscription event.
   * @method prepareNewsletterSubscriptionEventData
   * @memberof DataManager
   * @param {string} eventId - The unique ID for the event.
   * @returns {Object} The prepared event payload for NewsletterSubscription.
   */
  prepareNewsletterSubscriptionEventData(eventId) {
    const newsletterForm = document.querySelector("form#cust_news");
    const customData = {
      formData: new FormData(newsletterForm),
    };
    return this.prepareCommonPayload(EVENTS.Subscribe, customData, eventId);
  }

  /**
   * Prepares the payload for a ContactFormSubmission event.
   * @method prepareContactFormSubmissionEventData
   * @memberof DataManager
   * @param {string} eventId - The unique ID for the event.
   * @returns {Object} The prepared event payload for ContactFormSubmission.
   */
  prepareContactFormSubmissionEventData(eventId) {
    const contactForm = document.querySelector("form#contact");
    const customData = {
      formData: new FormData(contactForm),
    };
    return this.prepareCommonPayload(EVENTS.Contact, customData, eventId);
  }

  /**
   * Updates the stored hashed email and triggers full data refresh.
   * @async
   * @method updateUserEmail
   * @memberof DataManager
   * @param {string} email - The email to be hashed and stored.
   */
  async updateUserEmail(email) {
    const hashedEmail = await hashEmail(email);
    sessionStorage.setItem("user_email", hashedEmail);
    await this.updateUserData();
  }
}

export default DataManager;
